import { format } from 'date-fns'
import { supabase } from './supabase'
import { BoardCard, BoardColumn, CardFieldKey, CardLog, FIELD_DEFS, Option, toRowPatch, topPositionInColumn } from './boards'

export type ListTarget = 'notes' | 'tasks' | 'history'
export type ImportTarget = CardFieldKey | ListTarget

export type FieldMapping = Partial<Record<ImportTarget, number>>

export type ImportSettings = {
  mapping: FieldMapping
  stageColumn: number | null
  stageMap: Record<string, string | null>
  defaultColumnId: string
  idColumn: number | null
}

export type DatedEntry = { at: string | null; author: string | null; text: string }

export type ImportedTask =
  | { kind: 'log'; channel: CardLog['channel']; outcome: string; noAnswer: boolean; author: string | null }
  | { kind: 'task'; title: string; done: boolean; author: string | null }

export type ImportRowStatus =
  | 'new'
  | 'duplicate' // its ID was imported before and that card still exists
  | 'repeated' // its ID is on an earlier row of the same file
  | 'skipped_stage'

export type ImportRowPlan = {
  line: number
  values: Partial<BoardCard>
  notes: DatedEntry[]
  tasks: ImportedTask[]
  history: DatedEntry[]
  columnId: string | null
  importRef: string | null
  status: ImportRowStatus
  importedBefore: boolean
}

export type ImportContext = {
  tutors: { id: string; name: string }[]
  importedRefs: Set<string>
  // Import rows whose ID was imported before as new cards too. The earlier cards stay.
  includeImported?: boolean
}

export const MAX_IMPORT_ROWS = 2000

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

const LIST_LABELS: Record<ListTarget, string> = { notes: 'Notes', tasks: 'Tasks', history: 'History' }

const HEADER_GUESSES: Record<ImportTarget, string[]> = {
  parentName: ['parent', 'guardian', 'guardianname', 'familyname', 'name', 'clientname', 'contactname'],
  phone: ['phonenumber', 'mobile', 'cell', 'telephone', 'tel'],
  email: ['emailaddress', 'parentemail'],
  channel: ['camein', 'cameinby'],
  janeNumber: ['jane', 'janeno', 'janeclientnumber', 'janeclientnum'],
  studentFirstName: ['studentname', 'student', 'firstname'],
  grade: ['gradelevel'],
  subjects: ['subject'],
  mode: ['format'],
  area: ['location', 'neighbourhood', 'neighborhood'],
  source: ['heardfrom', 'referral', 'referralsource'],
  preferredTimes: ['availability', 'times'],
  wantedStartDate: ['startdate'],
  tutorId: ['tutor', 'tutorname'],
  rate: [],
  familyTold: [],
  paymentMethod: [],
  pauseReason: [],
  closeReason: [],
  notes: ['note', 'comments', 'comment'],
  tasks: ['task', 'todo', 'todos', 'calls', 'taskscallsemails'],
  history: ['activity', 'activitylog', 'events'],
}

const STAGE_HEADERS = ['stage', 'stagename', 'column', 'status', 'pipelinestage', 'boardcolumn']
const ID_HEADERS = ['id', 'importid', 'externalid']

const isListTarget = (t: ImportTarget): t is ListTarget => t in LIST_LABELS

function headerCandidates(target: ImportTarget): string[] {
  const own = isListTarget(target) ? [target] : [target, FIELD_DEFS[target].column, FIELD_DEFS[target].label]
  return own.map(norm).concat(HEADER_GUESSES[target])
}

export function guessSettings(headers: string[]): Pick<ImportSettings, 'mapping' | 'stageColumn' | 'idColumn'> {
  const normalized = headers.map(norm)
  const used = new Set<number>()
  const take = (match: (h: string) => boolean): number | null => {
    const i = normalized.findIndex((h, idx) => h !== '' && !used.has(idx) && match(h))
    if (i < 0) return null
    used.add(i)
    return i
  }

  const idColumn = take(h => ID_HEADERS.includes(h) || /(card|client|record|external|import)id$/.test(h))
  const stageColumn = take(h => STAGE_HEADERS.includes(h))
  const mapping: FieldMapping = {}
  for (const target of Object.keys(HEADER_GUESSES) as ImportTarget[]) {
    const candidates = headerCandidates(target)
    const i = take(h => candidates.includes(h))
    if (i !== null) mapping[target] = i
  }
  return { mapping, stageColumn, idColumn }
}

export function importedFieldLabels(mapping: FieldMapping): string[] {
  return (Object.keys(HEADER_GUESSES) as ImportTarget[])
    .filter(t => mapping[t] != null)
    .map(t => (isListTarget(t) ? LIST_LABELS[t] : FIELD_DEFS[t].label))
}

export function ignoredHeaders(headers: string[], s: Pick<ImportSettings, 'mapping' | 'stageColumn' | 'idColumn'>): string[] {
  const used = new Set<number | null>([s.stageColumn, s.idColumn, ...Object.values(s.mapping)])
  return headers.map((h, i) => (used.has(i) ? null : h || `Column ${i + 1}`)).filter(Boolean) as string[]
}

const STAGE_RULES: [RegExp, string][] = [
  [/no.?answer|no.?reply|voicemail/, 'no_answer'],
  [/first call/, 'first_call_done'],
  [/find(ing)?\s*(a\s*)?tutor/, 'finding_tutor'],
  [/payment due|overdue/, 'payment_due'],
  [/matched|awaiting payment/, 'tutor_matched'],
  [/^active$|paying|enrolled/, 'active'],
  [/pause|on hold/, 'paused'],
  [/clos|archiv|lost|not interested|cancel/, 'closed'],
  [/new|enquiry|inquiry|lead/, 'new_enquiry'],
]

export function guessColumnForStage(value: string, columns: BoardColumn[]): string | null {
  const v = norm(value)
  if (!v) return null
  const exact = columns.find(c => norm(c.name) === v || norm(c.key) === v)
  if (exact) return exact.id
  const text = value.toLowerCase()
  for (const [re, key] of STAGE_RULES) {
    const col = columns.find(c => c.key === key)
    if (col && re.test(text)) return col.id
  }
  return null
}

export function stageValues(rows: string[][], stageColumn: number): { value: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const r of rows) {
    const v = (r[stageColumn] ?? '').trim()
    counts.set(v, (counts.get(v) ?? 0) + 1)
  }
  return Array.from(counts, ([value, count]) => ({ value, count }))
}

export function guessStageMap(
  rows: string[][],
  stageColumn: number | null,
  columns: BoardColumn[],
  fallbackColumnId: string
): Record<string, string | null> {
  if (stageColumn == null) return {}
  const map: Record<string, string | null> = {}
  for (const { value } of stageValues(rows, stageColumn)) {
    map[value] = guessColumnForStage(value, columns) ?? fallbackColumnId
  }
  return map
}

const TRUE_WORDS = ['y', 'yes', 'true', '1', 'x']
const FALSE_WORDS = ['n', 'no', 'false', '0']

function matchOption(options: Option[], raw: string): string | null {
  const v = norm(raw)
  return options.find(o => norm(o.value) === v || norm(o.label) === v)?.value ?? null
}

export function parseImportDate(raw: string): string | null {
  const iso = raw.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  const d = iso ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) : new Date(raw)
  if (isNaN(d.getTime())) return null
  if (iso && d.getMonth() !== Number(iso[2]) - 1) return null
  return format(d, 'yyyy-MM-dd')
}

export function parseFieldValue(key: CardFieldKey, raw: string, tutors: ImportContext['tutors']): Partial<BoardCard> {
  const def = FIELD_DEFS[key]
  const set = (value: unknown) => ({ [key]: value } as Partial<BoardCard>)
  switch (def.type) {
    case 'select': {
      const value = matchOption(def.options ?? [], raw)
      if (value) return set(value)
      return key === 'closeReason' ? { closeReason: 'other', closeReasonOther: raw } : {}
    }
    case 'checkbox': {
      const v = raw.toLowerCase()
      if (TRUE_WORDS.includes(v)) return set(true)
      if (FALSE_WORDS.includes(v)) return set(false)
      return {}
    }
    case 'date': {
      const value = parseImportDate(raw)
      return value ? set(value) : {}
    }
    case 'tutor': {
      const v = raw.toLowerCase()
      const tutor = tutors.find(t => t.id === raw || t.name.trim().toLowerCase() === v)
      return tutor ? set(tutor.id) : {}
    }
    default:
      return set(raw)
  }
}

const DATED_LINE = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::\d{2})?(?:\s*UTC)?\s*\|\s*([^|]*?)\s*\|\s*(.*)$/

export function parseDatedEntries(cell: string): DatedEntry[] {
  const entries: DatedEntry[] = []
  for (const raw of cell.split('\n')) {
    const line = raw.trim()
    if (!line) continue
    const m = line.match(DATED_LINE)
    if (m) entries.push({ at: `${m[1]}T${m[2]}:00Z`, author: m[3] || null, text: m[4].trim() })
    else if (entries.length > 0) entries[entries.length - 1].text += `\n${line}`
    else entries.push({ at: null, author: null, text: line })
  }
  return entries.filter(e => e.text !== '')
}

const LOG_CHANNELS: Record<string, CardLog['channel']> = {
  call: 'call', phone: 'call', email: 'email', text: 'text', sms: 'text', whatsapp: 'whatsapp',
}

export function parseTaskLines(cell: string): ImportedTask[] {
  const tasks: ImportedTask[] = []
  for (const raw of cell.split('\n')) {
    const line = raw.trim()
    if (!line) continue
    const parts = line.split('|').map(p => p.trim())
    if (parts.length < 3) {
      tasks.push({ kind: 'task', title: line, done: false, author: null })
      continue
    }
    const [type, status, title, person] = parts
    const author = person || null
    const channel = LOG_CHANNELS[norm(type)]
    if (channel) tasks.push({ kind: 'log', channel, outcome: title || type, noAnswer: /no.?answer/i.test(title), author })
    else tasks.push({ kind: 'task', title: title || type, done: /^(done|completed?|closed)$/i.test(status), author })
  }
  return tasks
}

export function historyKind(text: string): 'created' | 'moved' | 'updated' {
  if (/^created\b/i.test(text)) return 'created'
  if (/^moved\b/i.test(text)) return 'moved'
  return 'updated'
}

export function buildImportPlan(rows: string[][], s: ImportSettings, ctx: ImportContext): ImportRowPlan[] {
  const seenRefs = new Set<string>()
  return rows.map((row, i) => {
    const cell = (idx: number | undefined) => (idx == null ? '' : (row[idx] ?? '').trim())
    const values: Partial<BoardCard> = {}
    for (const target of Object.keys(s.mapping) as ImportTarget[]) {
      if (isListTarget(target) || cell(s.mapping[target]) === '') continue
      Object.assign(values, parseFieldValue(target, cell(s.mapping[target]), ctx.tutors))
    }

    let columnId: string | null = s.defaultColumnId
    if (s.stageColumn != null) {
      const stage = cell(s.stageColumn)
      if (Object.prototype.hasOwnProperty.call(s.stageMap, stage)) columnId = s.stageMap[stage]
    }
    const importRef = s.idColumn == null ? null : cell(s.idColumn) || null

    const importedBefore = !!importRef && ctx.importedRefs.has(importRef)
    let status: ImportRowStatus = 'new'
    if (columnId == null) status = 'skipped_stage'
    else if (importRef && seenRefs.has(importRef)) status = 'repeated'
    else if (importedBefore && !ctx.includeImported) status = 'duplicate'
    if (importRef) seenRefs.add(importRef)

    return {
      line: i + 2,
      values,
      notes: parseDatedEntries(cell(s.mapping.notes)),
      tasks: parseTaskLines(cell(s.mapping.tasks)),
      history: parseDatedEntries(cell(s.mapping.history)),
      columnId,
      importRef,
      status,
      importedBefore,
    }
  })
}

const PAGE_SIZE = 1000
const INSERT_CHUNK = 100

export async function fetchImportedRefs(): Promise<Set<string>> {
  const refs = new Set<string>()
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('card_events')
      .select('ref:metadata->>import_ref')
      .not('metadata->>import_ref', 'is', null)
      .order('id')
      .range(from, from + PAGE_SIZE - 1)
    if (error) throw error
    for (const r of (data ?? []) as any[]) if (r.ref) refs.add(r.ref)
    if (!data || data.length < PAGE_SIZE) return refs
  }
}

export class ImportError extends Error {
  imported: number
  constructor(message: string, imported: number) {
    super(message)
    this.imported = imported
  }
}

export type ImportItem = Pick<ImportRowPlan, 'line' | 'values' | 'notes' | 'tasks' | 'history' | 'columnId' | 'importRef'>

export type ImportResult = {
  imported: number
  historyFailed: number
  detailsFailed: number
}

export async function importCards(
  boardId: string,
  items: ImportItem[],
  opts: { fileName: string; actorEmail: string | null },
  onProgress?: (done: number) => void
): Promise<ImportResult> {
  // On top of each column, like every new card, in the file's order.
  const nextPosition: Record<string, number> = {}
  for (const columnId of Array.from(new Set(items.map(i => i.columnId as string)))) {
    const count = items.filter(i => i.columnId === columnId).length
    nextPosition[columnId] = (await topPositionInColumn(columnId)) - (count - 1)
  }
  const prepared = items.map(item => ({
    item,
    row: {
      ...toRowPatch(item.values),
      board_id: boardId,
      column_id: item.columnId,
      position: nextPosition[item.columnId as string]++,
    } as Record<string, any>,
  }))
  const allColumns = new Set<string>()
  prepared.forEach(({ row }) => Object.keys(row).forEach(k => allColumns.add(k)))
  prepared.forEach(({ row }) =>
    allColumns.forEach(k => {
      if (!(k in row)) row[k] = k === 'family_told' ? false : null
    })
  )

  const result: ImportResult = { imported: 0, historyFailed: 0, detailsFailed: 0 }
  for (let i = 0; i < prepared.length; i += INSERT_CHUNK) {
    const chunk = prepared.slice(i, i + INSERT_CHUNK)
    const { data, error } = await supabase
      .from('board_cards')
      .insert(chunk.map(c => c.row))
      .select('id, column_id, position')
    if (error) throw new ImportError(error.message, result.imported)

    const idFor = new Map((data ?? []).map((r: any) => [`${r.column_id}:${r.position}`, r.id] as [string, string]))
    const saved = chunk
      .map(({ item, row }) => ({ item, cardId: idFor.get(`${row.column_id}:${row.position}`) as string }))
      .filter(c => c.cardId)
    const now = new Date().toISOString()

    const events = saved.flatMap(({ item, cardId }) => [
      ...item.history.map(h => ({
        card_id: cardId,
        kind: historyKind(h.text),
        summary: h.text,
        metadata: null,
        actor_email: h.author,
        created_at: h.at ?? now,
      })),
      {
        card_id: cardId,
        kind: 'updated',
        summary: `Imported from ${opts.fileName} (line ${item.line})`,
        metadata: { import_ref: item.importRef, import_file: opts.fileName, import_line: item.line },
        actor_email: opts.actorEmail,
        created_at: now,
      },
    ])
    const ev = await supabase.from('card_events').insert(events)
    if (ev.error) {
      console.error('Import history events failed:', ev.error)
      result.historyFailed += chunk.length
    } else {
      result.historyFailed += chunk.length - saved.length
    }

    const notes = saved.flatMap(({ item, cardId }) =>
      item.notes.map(n => ({ card_id: cardId, body: n.text, author_name: n.author ?? opts.actorEmail, created_at: n.at ?? now }))
    )
    const tasks = saved.flatMap(({ item, cardId }) =>
      item.tasks.flatMap(t =>
        t.kind === 'task'
          ? [{ card_id: cardId, title: t.author ? `${t.title} (${t.author})` : t.title, done: t.done, done_at: t.done ? now : null }]
          : []
      )
    )
    const logs = saved.flatMap(({ item, cardId }) =>
      item.tasks.flatMap(t =>
        t.kind === 'log'
          ? [{ card_id: cardId, channel: t.channel, direction: 'out', outcome: t.outcome, no_answer: t.noAnswer, note: t.author ? `By ${t.author}` : null }]
          : []
      )
    )
    const failedCards = new Set<string>()
    for (const [table, rows] of [['card_notes', notes], ['card_tasks', tasks], ['card_logs', logs]] as const) {
      if (rows.length === 0) continue
      const { error: e } = await supabase.from(table).insert(rows as any[])
      if (e) {
        console.error(`Import ${table} failed:`, e)
        rows.forEach((r: any) => failedCards.add(r.card_id))
      }
    }
    result.detailsFailed += failedCards.size

    result.imported += chunk.length
    onProgress?.(result.imported)
  }
  return result
}
